import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  IFieldWorker,
  IFieldWorkerCreatePayload,
  IFieldWorkerUpdatePayload,
  ICertificationMaster,
  IComplianceMaster,
} from "../../../types/api/fieldWorker";
import {
  listFieldWorkers,
  getFieldWorker,
  createFieldWorker,
  updateFieldWorker,
  deleteFieldWorker,
  listMasterCertifications,
  listMasterCompliances,
  listWorkerLocations,
} from "./callbacks";

export const useFieldWorkerDetail = (id: number | null) =>
  useQuery<TResponse<IFieldWorker>, Error>({
    queryKey: [API_ENDPOINTS.FIELD_WORKER_DETAIL, id],
    queryFn: () => getFieldWorker(id!).then((r) => r.data),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

export const useFieldWorkerList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery<TResponseWithQData<IFieldWorker[]>, Error>({
    queryKey: [API_ENDPOINTS.FIELD_WORKER_LIST, params],
    queryFn: () => listFieldWorkers(params).then((r) => r.data),
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useFieldWorkerCreate = (
  options?: { onSuccess?: (d: IFieldWorker) => void; onError?: (e: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<IFieldWorker>, Error, IFieldWorkerCreatePayload>({
    mutationFn: (data) => createFieldWorker(data).then((r) => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.FIELD_WORKER_LIST] });
      options?.onSuccess?.(res.data as IFieldWorker);
    },
    onError: options?.onError,
  });
};

export const useFieldWorkerUpdate = (
  options?: { onSuccess?: (d: IFieldWorker) => void; onError?: (e: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<IFieldWorker>, Error, IFieldWorkerUpdatePayload>({
    mutationFn: (data) => updateFieldWorker(data).then((r) => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.FIELD_WORKER_LIST] });
      options?.onSuccess?.(res.data as IFieldWorker);
    },
    onError: options?.onError,
  });
};

export const useFieldWorkerDelete = (
  options?: { onSuccess?: () => void; onError?: (e: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<null>, Error, number>({
    mutationFn: (id) => deleteFieldWorker(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.FIELD_WORKER_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useMasterCertifications = (
  options?: Omit<UseQueryOptions<TResponse<ICertificationMaster[]>, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<TResponse<ICertificationMaster[]>, Error>({
    queryKey: [API_ENDPOINTS.MASTER_CERTIFICATIONS],
    queryFn: () => listMasterCertifications().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useMasterCompliances = (
  options?: Omit<UseQueryOptions<TResponse<IComplianceMaster[]>, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<TResponse<IComplianceMaster[]>, Error>({
    queryKey: [API_ENDPOINTS.MASTER_COMPLIANCES],
    queryFn: () => listMasterCompliances().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useWorkerLocations = (
  options?: Omit<UseQueryOptions<TResponse<any[]>, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<TResponse<any[]>, Error>({
    queryKey: [API_ENDPOINTS.FIELD_WORKER_LOCATIONS],
    queryFn: () => listWorkerLocations().then((r) => r.data),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    ...options,
  });

// Latest GPS position per worker — auto-refreshes every 30s
export const useEmployeeLocations = (
  options?: Omit<UseQueryOptions<TResponse<any[]>, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<TResponse<any[]>, Error>({
    queryKey: [API_ENDPOINTS.EMPLOYEE_LOCATIONS_LATEST],
    queryFn: () =>
      import("../../../lib/axios")
        .then((m) => m.default.get(API_ENDPOINTS.EMPLOYEE_LOCATIONS_LATEST))
        .then((r: any) => r.data),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    ...options,
  });
